import { INotSure } from "@/types";
import {
  boundaryDS,
  dashboardDS,
  dateFormats,
  expiryTime,
  lastUpdated,
  locationBoundaryDS,
  maxValueDS,
} from "@/utils/Constant";
import dayjs from "dayjs";
import { del, get as idxGet, set as idxSet } from "idb-keyval";
import Cookies from "js-cookie";

/**
 * @function storeValue
 * @description function to store values by key in the web cookie storage
 */
export const storeValue = (
  key: string,
  value: INotSure,
  expires = expiryTime
) => {
  Cookies.set(key, value, { expires });
};

/**
 * @function getValue
 * @description function to get values by key in the web cookie storage
 */
export const getValue = (key: string): string | undefined => {
  return Cookies.get(key);
};

/**
 * @function removeValue
 * @description function to remove values by key in the web cookie storage
 */
export const removeValue = (key: string) => {
  Cookies.remove(key);
};

/**
 * @function storeLSValue
 * @description function to store values by key in local storage
 */
export const storeLSValue = <T>(key: string, value: T) => {
  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * @function getLSValue
 * @description function to get values by key from local storage
 */
export const getLSValue = <T>(key: string): T | null => {
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) : null;
};

/**
 * @function removeLSValue
 * @description function to remove values by key from local storage
 */
export const removeLSValue = (key: string) => {
  localStorage.removeItem(key);
};

/**
 * @function storeIdxValue
 * @description function to store values by key in IndexedDB
 */
export const storeIdxValue = async <T>(
  key: string,
  value: T
  // expiry = staleTimeExpiry
) => {
  try {
    // await idxSet(key, { value, expiry });
    await idxSet(key, value);
  } catch (error) {
    console.error(`Error storing value in IndexedDB: ${error}`);
  }
};

/**
 * @function getIdxValue
 * @description function to get values by key from IndexedDB
 */
export const getIdxValue = async <T>(key: string): Promise<T | null> => {
  try {
    const storedValue = await idxGet(key);
    if (!storedValue) return null;

    return storedValue as T;
  } catch (error) {
    console.error(`Error getting value from IndexedDB: ${error}`);
    return null;
  }
};

export interface ILastUpdated {
  total_count: number;
  last_updated: string;
  phc_last_updated: string;
  rev_last_updated: string;
  data_last_updated: string;
}

export const keepDataFresh = async (resultData: ILastUpdated) => {
  try {
    const storedData = await getIdxValue<{ data_last_updated: string }>(
      lastUpdated
    );
    if (!storedData) return null;

    // check if the stored data is different from the new data last_updated field
    const storedDate = dayjs(
      storedData.data_last_updated,
      dateFormats.DATE_TIME
    );
    const newDate = dayjs(resultData.data_last_updated, dateFormats.DATE_TIME);
    // check if the stored data is different from the new data last_updated field
    const isDifferent = storedDate.isBefore(newDate);
    if (!isDifferent) return null;

    await Promise.all([
      removeIdxValue(dashboardDS),
      removeIdxValue(maxValueDS),
      removeIdxValue(boundaryDS),
      removeIdxValue(locationBoundaryDS),
      removeIdxValue(lastUpdated),
      // window.location.reload(),
    ]);

    // Update IndexedDB with the latest result data
    await storeIdxValue(lastUpdated, resultData);
    return null;
  } catch (error) {
    console.error(`Error keeping data fresh in IndexedDB: ${error}`);
    return null;
  }
};

/**
 * @function removeIdxValue
 * @description function to remove values by key from IndexedDB
 */
export const removeIdxValue = async (key: string) => {
  try {
    await del(key);
  } catch (error) {
    console.error(`Error removing value from IndexedDB: ${error}`);
  }
};
